import { Controller } from "@hotwired/stimulus";
import { MDCDialog } from "@material/dialog";
import Rails from "@rails/ujs";

let activeDialogRequest = null;

export default class extends Controller {
  static targets = ["openDialogButton", "dialog", "closeButton"];

  connect() {
    this.closeButtonTargets.forEach((button) => {
      if (button.dataset.dialogCloseBound === "true") return;

      button.dataset.dialogCloseBound = "true";
      button.addEventListener("click", (event) => {
        this.closeDialog(event);
      });
    });

    console.log("Connect Dialog controller");
  }

  dialogTargetDisconnected(dialogEl) {
    // Moving a dialog to the body keeps it connected; removing it must cancel
    // MDC's opening animation before its delayed focus trap runs.
    if (dialogEl.isConnected) return;

    dialogEl.dialogAnchorCleanup?.();

    const dialog = dialogEl.MDCDialog;
    if (dialog == null) return;
    dialog.close();
    dialog.destroy();
  }

  initialize() {
    this.dialogTargets.forEach((dialogEl) => {
      const shell = dialogEl.closest('.mdc-dialog-shell') || dialogEl;
      if (dialogEl.dataset.dialogAutoOpen === 'true' && shell.parentElement !== document.body) document.body.appendChild(shell);
      const dialog = dialogEl.MDCDialog || new MDCDialog(dialogEl);
      dialog.root.MDCDialog = dialog;

      const submitButtons = Array.from(dialogEl.querySelectorAll("#dialog-submit-button, [data-mdc-dialog-save-button='true']"));
      const fileUploadButton = dialogEl.querySelector("#file-upload-btn");

      submitButtons.forEach((submitButton) => {
        submitButton.addEventListener("click", function () {
          const form = dialogEl.querySelector(".mdc-dialog__content form.dialog-submit-form");

          if (form !== null && form.reportValidity()) {
            dialogEl.dataset.dialogSubmitting = "true";
            submitButton.disabled = true;
            if (form.dataset.remote && form.dataset.remote !== false) {
              Rails.fire(form, "submit");

              form.addEventListener("ajax:success", function (event) {
                const [_data, _status, xhr] = event.detail;
                const response_html = xhr.responseText;

                if (form.dataset.replaceOuterId != null) {
                  const replacementTarget = document.getElementById(form.dataset.replaceOuterId);
                  if (replacementTarget != null) replacementTarget.outerHTML = response_html;
                } else if (form.dataset.replaceId != null) {
                  document.getElementById(form.dataset.replaceId).innerHTML = response_html;
                }

                dialog.close();
              });

              form.addEventListener("ajax:error", function () {
                dialogEl.dataset.dialogSubmitting = "false";
                submitButton.disabled = false;

                const trackedForm = dialogEl.querySelector(".dialog-submit-form[id]");
                if (trackedForm != null) {
                  this.updateDialogSaveButtons(
                    dialogEl,
                    trackedForm,
                    submitButtons,
                    dialogEl.dataset.dialogInitialFormState || "",
                    dialogEl.dataset.dialogInitialFileState || ""
                  );
                }
              }.bind(this));
            } else if (form.dataset.turbo === "true" && form.requestSubmit) {
              form.requestSubmit();
            } else {
              form.submit();
            }
          }
        });
      });

      if (fileUploadButton != null && fileUploadButton.tagName !== "LABEL" && fileUploadButton.getAttribute("for") == null) {
        fileUploadButton.addEventListener("click", function () {
          const fileInput = dialogEl.querySelector("input[type=file]");
          fileInput.click();
        });
      }

      const dialogShell = dialogEl.closest(".mdc-dialog-shell") || dialogEl;
      this.openDialog(dialog, {shouldOpen: dialogEl.dataset.dialogAutoOpen === "true", preexisting: dialogShell.dataset.dialogTransient !== "true"});
    });

    this.openDialogButtonTargets.forEach((btn) => {
      if (btn.dataset.dialogOpenBound === "true") return;
      btn.dataset.dialogOpenBound = "true";

      btn.querySelectorAll("a[target='_blank']").forEach((link) => {
        link.addEventListener("click", (event) => event.stopPropagation());
      });

      if (btn.dataset.dialogIframeUrl != null) {
        btn.addEventListener("click", (event) => {
          event.preventDefault();
          this.openIframeDialog(btn.dataset.dialogIframeUrl, btn);
        });
        return;
      }

      if (btn.dataset.remote === "true") {
        btn.addEventListener("ajax:success", (event) => {
          const [_data, _status, xhr] = event.detail;
          this.openDialogHtml(xhr.responseText, btn);
        });
      } else {
        btn.addEventListener("click", (event) => {
          const nestedLink = event.target.closest("a");
          if (nestedLink != null && nestedLink !== btn) return;

          const href = btn.getAttribute("href") || btn.dataset.dialogUrl;
          if (href == null || href.length === 0) return;
          if (btn.dataset.method != null || btn.dataset.turboMethod != null || btn.dataset.confirm != null) return;

          event.preventDefault();

          const existingDialog = btn.dataset.dialogReuseId && document.getElementById(btn.dataset.dialogReuseId);
          if (btn.dataset.dialogReuseId) btn.closest(".mdc-dialog")?.MDCDialog?.close();
          if (existingDialog) {
            this.openExistingDialog(existingDialog, btn);
            return;
          }

          if (href.startsWith("#")) {
            const dialogEl = document.querySelector(href);
            if (dialogEl != null) {
              this.openExistingDialog(dialogEl, btn);
            }
            return;
          }

          this.fetchDialogHtml(href, btn);
        });

        if (btn.tagName !== "A" && btn.tagName !== "BUTTON") {
          btn.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            btn.click();
          });
        }
      }
    });
  }

  fetchDialogHtml(url, btn) {
    activeDialogRequest?.abort();
    const request = new AbortController();
    activeDialogRequest = request;

    fetch(url, {
      credentials: "same-origin",
      cache: "no-store",
      signal: request.signal,
      headers: {
        Accept: "text/html, application/xhtml+xml",
        "X-Requested-With": "XMLHttpRequest",
      },
    }).then((response) => {
      if (!response.ok) {
        window.location = url;
        return null;
      }

      return response.text();
    }).then((html) => {
      if (html == null || activeDialogRequest !== request) return;
      this.openDialogHtml(html, btn);
    }).catch(() => {
      if (request.signal.aborted) return;
      window.location = url;
    }).finally(() => {
      if (activeDialogRequest === request) activeDialogRequest = null;
    });
  }

  openDialogHtml(dialogHtml, btn) {
    const div = document.createElement("div");
    div.innerHTML = dialogHtml;
    const dialogEl = div.firstElementChild;
    if (dialogEl == null) return;

    dialogEl.dataset.dialogTransient = "true";
    this.openDialogElement(dialogEl, btn);
  }

  openExistingDialog(dialogEl, btn) {
    const dialogShell = dialogEl.classList.contains("mdc-dialog-shell") ? dialogEl : dialogEl.closest(".mdc-dialog-shell") || dialogEl;
    if (dialogShell == null) return;

    this.openDialogElement(dialogShell, btn, {preexisting: true});
  }

  openExistingDialogForButton(event) {
    event.preventDefault();

    const selector = event.currentTarget.getAttribute("href");
    if (selector == null || !selector.startsWith("#")) return;

    const dialogEl = document.querySelector(selector);
    if (dialogEl != null) this.openExistingDialog(dialogEl, event.currentTarget);
  }

  openDialogElement(dialogShell, btn, options = {}) {
    if (btn?.dataset.rtl == "true") {
      dialogShell.classList.add("rtl");
      dialogShell.setAttribute("dir", "rtl");
    }

    document.body.appendChild(dialogShell);

    const dialog = this.getDialogInstance(dialogShell);
    if (dialog == null) return;

    if (btn?.dataset.dialogAnchor === "true") this.anchorDialog(dialog, btn);

    if (dialog.root.dataset.dialogInitialized === "true") {
      this.prepareCommentsDialog(dialog.root);
      dialog.open();
      window.setTimeout(() => this.scrollDialogContentToBottom(dialog.root), 300);
    } else {
      this.openDialog(dialog, options);
    }
  }

  openIframeDialog(url, btn) {
    const title = btn?.dataset.dialogIframeTitle || "Preview";
    const iframeUrl = this.buildIframeUrl(url);
    const template = document.querySelector("#iframe-dialog-template");
    if (template == null) return;

    const dialogShell = template.content.firstElementChild?.cloneNode(true);
    if (dialogShell == null) return;

    const dialogTitle = dialogShell.querySelector('[data-iframe-dialog-target="title"]');
    const iframe = dialogShell.querySelector('[data-iframe-dialog-target="iframe"]');
    if (dialogTitle != null) {
      dialogTitle.textContent = title;
    }
    if (iframe != null) {
      iframe.setAttribute("src", iframeUrl);
      iframe.setAttribute("title", title);
    }

    document.body.appendChild(dialogShell);

    const dialog = this.getDialogInstance(dialogShell);
    if (dialog == null) return;

    this.openDialog(dialog);
  }

  anchorDialog(dialog, button) {
    const root = dialog.root;
    root.dialogAnchorCleanup?.();
    const surface = root.querySelector(".mdc-dialog__surface");
    const position = () => {
      const anchored = window.innerWidth > 640 && button.isConnected;
      root.classList.toggle("dialog-anchored", anchored);
      if (!anchored) return;

      const anchor = button.getBoundingClientRect();
      const width = surface.offsetWidth;
      const height = surface.offsetHeight;
      const left = Math.max(16, Math.min(anchor.left, window.innerWidth - width - 16));
      const below = anchor.bottom + 8;
      const top = below + height <= window.innerHeight - 16 ? below : Math.max(16, anchor.top - height - 8);
      root.style.setProperty("--dialog-left", `${left}px`);
      root.style.setProperty("--dialog-top", `${top}px`);
    };
    const observer = new ResizeObserver(position);
    observer.observe(surface);
    window.addEventListener("resize", position);
    dialog.listen("MDCDialog:opened", position);
    const cleanup = () => {
      observer.disconnect();
      window.removeEventListener("resize", position);
      dialog.unlisten("MDCDialog:opened", position);
      dialog.unlisten("MDCDialog:closed", cleanup);
      root.dialogAnchorCleanup = null;
    };
    root.dialogAnchorCleanup = cleanup;
    dialog.listen("MDCDialog:closed", cleanup);
    position();
  }

  getDialogInstance(dialogShell) {
    const dialogEl = dialogShell.classList.contains("mdc-dialog")
      ? dialogShell
      : dialogShell.querySelector(".mdc-dialog");

    if (dialogEl == null) return null;

    const dialog = dialogEl.MDCDialog || new MDCDialog(dialogEl);
    dialogEl.MDCDialog = dialog;
    dialog.root.MDCDialog = dialog;
    return dialog;
  }

  buildIframeUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const host = parsed.hostname.replace(/^www\./, "");

      if (host.includes("youtube.com")) {
        const videoId = parsed.searchParams.get("v");
        if (videoId) {
          const start = parsed.searchParams.get("t");
          const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
          embed.searchParams.set("autoplay", "1");
          embed.searchParams.set("mute", "1");
          embed.searchParams.set("playsinline", "1");
          if (start != null && start.length > 0) {
            embed.searchParams.set("start", this.youtubeStartSeconds(start).toString());
          }
          return embed.toString();
        }
      }

      if (host === "youtu.be") {
        const videoId = parsed.pathname.replace(/^\//, "");
        if (videoId.length > 0) {
          const start = parsed.searchParams.get("t");
          const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
          embed.searchParams.set("autoplay", "1");
          embed.searchParams.set("mute", "1");
          embed.searchParams.set("playsinline", "1");
          if (start != null && start.length > 0) {
            embed.searchParams.set("start", this.youtubeStartSeconds(start).toString());
          }
          return embed.toString();
        }
      }

      if (host.includes("vimeo.com")) {
        const match = parsed.pathname.match(/\/(\d+)/);
        if (match != null && match[1] != null) {
          const embed = new URL(`https://player.vimeo.com/video/${match[1]}`);
          embed.searchParams.set("autoplay", "1");
          embed.searchParams.set("muted", "1");
          embed.searchParams.set("playsinline", "1");
          return embed.toString();
        }
      }
    } catch (_error) {
      // Fall through to the original URL.
    }

    return url;
  }

  youtubeStartSeconds(value) {
    if (value == null || value.length === 0) return 0;
    if (/^\d+$/.test(value)) return parseInt(value, 10);

    const match = value.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
    if (match == null) return 0;

    const hours = parseInt(match[1] || "0", 10);
    const minutes = parseInt(match[2] || "0", 10);
    const seconds = parseInt(match[3] || "0", 10);
    return (hours * 3600) + (minutes * 60) + seconds;
  }

  scrollDialogContentToBottom(dialogRoot) {
    const content = dialogRoot.querySelector(".comments-dialog__content")
      || dialogRoot.querySelector("[data-dialog-scroll-to-bottom='true']");
    if (content == null) return;

    const scrollToBottom = () => {
      if (content.isConnected) content.scrollTop = content.scrollHeight;
    };
    const focusComposer = () => {
      const input = dialogRoot.querySelector(".admin-comment-composer textarea");
      input?.focus({preventScroll: true});
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToBottom();
        focusComposer();
        dialogRoot.classList.remove("comments-dialog--positioning");
      });
    });
    window.setTimeout(() => {
      scrollToBottom();
      focusComposer();
      dialogRoot.classList.remove("comments-dialog--positioning");
    }, 150);
  }

  prepareCommentsDialog(dialogRoot) {
    if (dialogRoot.querySelector(".comments-dialog__content") != null) {
      dialogRoot.classList.add("comments-dialog--positioning");
    }
  }

  openDialog(dialog, options = {}) {
    const shouldOpen = options.shouldOpen !== false;
    const isPreexistingDialog = options.preexisting === true;
    const dialogRoot = dialog.root;
    const dialogShell = dialogRoot.closest(".mdc-dialog-shell") || dialogRoot;
    dialogRoot.dataset.dialogInitialized = "true";
    dialogRoot.addEventListener("toggle", () => this.layoutDialogFields(dialogRoot), true);
    dialogRoot.addEventListener("inline-edit:opened", (event) => {
      const form = event.detail.form;
      form.dataset.inlineInitialState = this.serializeForm(form);
      const buttons = Array.from(form.querySelectorAll("[data-inline-edit-save]"));
      buttons.forEach(button => { button.hidden = false; });
      this.updateDialogSaveButtons(dialogRoot, form, buttons, form.dataset.inlineInitialState);
      this.layoutDialogFields(dialogRoot);
    });
    const updateInlineForm = (event) => {
      const form = event.target.closest?.("form[data-inline-edit-form]");
      if (!form || form.hidden) return;
      this.updateDialogSaveButtons(dialogRoot, form, Array.from(form.querySelectorAll("[data-inline-edit-save]")), form.dataset.inlineInitialState);
      this.preventDialogClosing(dialog);
    };
    dialogRoot.addEventListener("input", updateInlineForm);
    dialogRoot.addEventListener("change", updateInlineForm);
    const resetInlineState = () => {
      dialogRoot.dataset.dialogDirty = "false";
      dialog.escapeKeyAction = "close";
      dialog.scrimClickAction = "close";
    };
    dialogRoot.addEventListener("inline-edit:closed", resetInlineState);
    dialogRoot.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !dialogRoot.querySelector("form[data-inline-edit-form]:not([hidden])")) return;
      this.handleDialogCloseRequest({dialog, dialogRoot, event});
    }, true);

    dialogRoot.addEventListener("turbo:submit-end", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      dialogRoot.dataset.dialogSubmitting = "false";
      if (form.hasAttribute("data-inline-edit-form")) {
        if (event.detail?.success === true) resetInlineState();
        else this.updateDialogSaveButtons(dialogRoot, form, Array.from(form.querySelectorAll("[data-inline-edit-save]")), form.dataset.inlineInitialState);
        return;
      }
      if (trackedForm) {
        this.updateDialogSaveButtons(dialogRoot, trackedForm, submitButtons,
          dialogRoot.dataset.dialogInitialFormState || "", dialogRoot.dataset.dialogInitialFileState || "");
      }
      if (event.detail.fetchResponse?.response.headers.get("X-Dialog-Confirmation-Required") === "true") return;
      if (event.detail?.success === true) {
        window.requestAnimationFrame(() => {
          if (form.classList.contains("admin-comment-composer")) {
            trackedForm = this.resetCommentComposerState(dialogRoot, dialog);
            this.scrollToNewComment(dialogRoot);
          } else {
            this.scrollDialogContentToBottom(dialogRoot);
          }
        });
      }
      if (form.dataset.dialogCloseOnSuccess !== "true") return;
      if (!event.detail || event.detail.success !== true) return;

      dialog.close();
    });
    dialogRoot.addEventListener("click", function (event) {
      const action = event.target instanceof Element && event.target.closest("[data-dialog-close-on-click='true']");
      if (action != null) dialog.close();
    });

    const dialogSaveButton = dialogRoot.querySelector("#dialog-submit-button, [data-mdc-dialog-save-button='true']");
    const candidateTrackedForm = dialogSaveButton?.form
      || dialogRoot.querySelector(".mdc-dialog__content form.dialog-submit-form")
      || dialogRoot.querySelector(".mdc-dialog__content form")
      || dialogRoot.querySelector("form")
      || null;
    let trackedForm = dialogRoot.dataset.dialogSkipUnsavedConfirmation === "true" ? null : candidateTrackedForm;
    const hasTrackedForm = trackedForm != null;
    const submitButtons = Array.from(dialogRoot.querySelectorAll("#dialog-submit-button, [data-mdc-dialog-save-button='true']"));

    dialogRoot.dataset.dialogHasTrackedForm = hasTrackedForm ? "true" : "false";
    dialogRoot.dataset.dialogDirty = "false";
    dialogRoot.classList.add("dialog-pristine");

    const clearPristineInvalidState = () => {
      if (!dialogRoot.classList.contains("dialog-pristine")) return;

      dialogRoot.querySelectorAll(".mdc-text-field--invalid, .mdc-select--invalid").forEach((field) => {
        field.classList.remove("mdc-text-field--invalid", "mdc-select--invalid");
      });
    };

    const restoreRequiredState = () => {
      trackedForm?.querySelectorAll("[data-dialog-required-original='true']").forEach((field) => {
        field.setAttribute("required", "required");
        field.removeAttribute("data-dialog-required-original");
      });
    };

    if (hasTrackedForm) {
      dialogRoot.dataset.dialogInitialFormState = this.serializeForm(trackedForm);
      dialogRoot.dataset.dialogInitialFileState = this.serializeFileInputs(trackedForm);

      trackedForm.querySelectorAll("input[required], select[required], textarea[required]").forEach((field) => {
        field.dataset.dialogRequiredOriginal = "true";
        field.removeAttribute("required");
      });

      this.updateDialogSaveButtons(
        dialogRoot,
        trackedForm,
        submitButtons,
        dialogRoot.dataset.dialogInitialFormState,
        dialogRoot.dataset.dialogInitialFileState
      );

      const updateDirtyState = () => {
        const initialFormState = dialogRoot.dataset.dialogInitialFormState || "";
        const initialFileState = dialogRoot.dataset.dialogInitialFileState || "";
        const isDirty = this.serializeForm(trackedForm) !== initialFormState || this.serializeFileInputs(trackedForm) !== initialFileState;
        dialogRoot.dataset.dialogDirty = isDirty ? "true" : "false";
        this.updateDialogSaveButtons(dialogRoot, trackedForm, submitButtons, initialFormState, initialFileState);

        if (isDirty) {
          this.preventDialogClosing(dialog);
        }
      };

      const markFieldTouched = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const field = target.closest(".mdc-text-field, .mdc-select");
        if (field != null) {
          field.classList.add("dialog-field-touched");
          dialogRoot.classList.remove("dialog-pristine");
          restoreRequiredState();
        }
      };

      const stripInvalidClasses = () => {
        if (!dialogRoot.classList.contains("dialog-pristine")) return;

        dialogRoot.querySelectorAll(".mdc-text-field--invalid, .mdc-select--invalid").forEach((field) => {
          field.classList.remove("mdc-text-field--invalid", "mdc-select--invalid");
        });
      };

      trackedForm.addEventListener("input", (event) => {
        markFieldTouched(event);
        updateDirtyState();
      });
      trackedForm.addEventListener("change", (event) => {
        markFieldTouched(event);
        updateDirtyState();
      });
      trackedForm.addEventListener("blur", markFieldTouched, true);

      trackedForm.querySelectorAll(".mutation_observer").forEach((element) => {
        const observer = new MutationObserver(updateDirtyState);
        const config = { attributes: false, childList: true, characterData: true };
        observer.observe(element, config);
      });

      const invalidObserver = new MutationObserver(() => {
        stripInvalidClasses();
      });
      invalidObserver.observe(trackedForm, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      dialogRoot.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        restoreRequiredState();
        this.handleDialogCloseRequest({
          dialog,
          dialogRoot,
          trackedForm,
          dialogSaveButton,
          submitButtons,
          event,
        });
      }, true);

      window.requestAnimationFrame(() => {
        clearPristineInvalidState();
        stripInvalidClasses();
      });
    }

    dialogRoot.addEventListener("click", (event) => {
      const closeButton = event.target instanceof Element && event.target.closest(".dialog-close-button");
      const clickedScrim = event.target instanceof Element && event.target.classList.contains("mdc-dialog__scrim");
      if (!closeButton && !clickedScrim) return;

      restoreRequiredState();
      this.handleDialogCloseRequest({
        dialog,
        dialogRoot,
        trackedForm,
        dialogSaveButton,
        submitButtons,
        event,
      });
    });

    dialog.listen("MDCDialog:opening", () => {
      dialogRoot.querySelectorAll("[data-dialog-reset-on-open='true']").forEach((form) => {
        if (typeof form.reset === "function") {
          form.reset();
        }

        const keyInput = form.querySelector("[data-autocomplete-target='key']");
        const itemIdInput = form.querySelector("[data-autocomplete-target='itemId']");
        const submitButton = form.querySelector("[data-autocomplete-target='submit']");
        const outputList = form.querySelector("[data-autocomplete-target='output']");

        if (keyInput != null) keyInput.value = "";
        if (itemIdInput != null) itemIdInput.value = "";
        if (submitButton != null) submitButton.disabled = true;
        if (outputList != null) outputList.innerHTML = "";
      });
    });

    dialog.listen("MDCDialog:opened", () => {
      this.layoutDialogFields(dialogRoot);
      if (hasTrackedForm) {
        window.requestAnimationFrame(() => {
          if (dialogRoot.dataset.dialogDirty === "true") return;
          dialogRoot.dataset.dialogInitialFormState = this.serializeForm(trackedForm);
          dialogRoot.dataset.dialogInitialFileState = this.serializeFileInputs(trackedForm);
          dialogRoot.dataset.dialogDirty = "false";
          this.updateDialogSaveButtons(
            dialogRoot,
            trackedForm,
            submitButtons,
            dialogRoot.dataset.dialogInitialFormState,
            dialogRoot.dataset.dialogInitialFileState
          );
        });
      }
      this.focusInitialDialogElement(dialogRoot);
      this.scrollDialogContentToBottom(dialogRoot);
    });

    dialog.listen("MDCDialog:closing", function () {});

    dialog.listen("MDCDialog:closed", function () {
      dialogRoot.querySelectorAll("input").forEach(function (el) {
        el.blur();
      });
      if (!isPreexistingDialog && dialogShell.isConnected) dialogShell.remove();
    });

    if (shouldOpen) {
      this.prepareCommentsDialog(dialogRoot);
      dialog.open();
      window.setTimeout(() => this.scrollDialogContentToBottom(dialogRoot), 300);
    }
  }

  closeDialog(event) {
    const dialogRoot = event.currentTarget.closest(".mdc-dialog");
    if (dialogRoot == null) return;

    const dialog = dialogRoot.MDCDialog;
    if (dialog == null) {
      dialogRoot.closest(".mdc-dialog-shell")?.remove();
      return;
    }

    const submitButtons = Array.from(dialogRoot.querySelectorAll("#dialog-submit-button, [data-mdc-dialog-save-button='true']"));
    const trackedForm = submitButtons[0]?.form
      || dialogRoot.querySelector(".mdc-dialog__content form.dialog-submit-form")
      || dialogRoot.querySelector(".mdc-dialog__content form")
      || dialogRoot.querySelector("form");
    const dialogSaveButton = submitButtons[0] || null;
    this.handleDialogCloseRequest({
      dialog,
      dialogRoot,
      trackedForm,
      dialogSaveButton,
      submitButtons,
      event,
    });
  }

  focusInitialDialogElement(dialogRoot) {
    const inlineInput = dialogRoot.querySelector("form[data-inline-edit-form]:not([hidden]) input:not([type='hidden']), form[data-inline-edit-form]:not([hidden]) textarea");
    if (inlineInput instanceof HTMLElement) {
      inlineInput.focus({preventScroll: true});
      return;
    }
    const dialogContent = dialogRoot.querySelector(".mdc-dialog__content");
    const dialogActions = dialogRoot.querySelector(".mdc-dialog__actions");
    const commentComposerInput = dialogRoot.querySelector(".admin-comment-composer textarea");

    if (commentComposerInput instanceof HTMLElement) {
      commentComposerInput.focus({preventScroll: true});
      return;
    }

    const initialFocusTarget = dialogRoot.querySelector("[data-dialog-initial-focus]");
    if (initialFocusTarget instanceof HTMLElement) {
      initialFocusTarget.focus({preventScroll: true});
      return;
    }

    const autofocusCandidate = dialogRoot.querySelector(
      "[autofocus='autofocus'], [autofocus], textarea[autofocus='autofocus'], input[autofocus='autofocus'], select[autofocus='autofocus'], button[autofocus='autofocus']"
    );

    if (autofocusCandidate instanceof HTMLElement) {
      autofocusCandidate.focus();
      return;
    }

    const contentFocusable = dialogContent?.querySelector(
      "input:not([type='hidden']):not([disabled]):not([hidden]), textarea:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), .mdc-select:not(.mdc-select--disabled) .mdc-select__anchor, [contenteditable='true'], button:not(.dialog-close-button):not([disabled]):not([hidden]), a[href]"
    );

    if (contentFocusable instanceof HTMLElement) {
      contentFocusable.focus();
      return;
    }

    const actionFocusable = dialogActions?.querySelector(
      "button:not(.dialog-close-button):not([disabled]):not([hidden]), a[href]"
    );

    if (actionFocusable instanceof HTMLElement) {
      actionFocusable.focus();
      return;
    }

    const fallback = dialogContent?.querySelector(".mdc-dialog__content-inner") || dialogRoot.querySelector(".mdc-dialog__surface");
    if (fallback instanceof HTMLElement) {
      fallback.setAttribute("tabindex", "-1");
      fallback.focus();
    }
  }

  layoutDialogFields(dialogRoot) {
    window.requestAnimationFrame(() => {
      dialogRoot.querySelectorAll(".mdc-text-field").forEach((fieldEl) => {
        fieldEl.MDCTextField?.layout?.();
      });

      dialogRoot.querySelectorAll(".mdc-select").forEach((selectEl) => {
        selectEl.MDCSelect?.layout?.();
        selectEl.MDCSelect?.layoutOptions?.();
      });
    });
  }

  handleDialogCloseRequest({ dialog, dialogRoot, trackedForm, dialogSaveButton, submitButtons, event }) {
    if (event != null) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (dialogRoot.dataset.dialogConfirmCloseButton === "false" && event?.target instanceof Element && event.target.closest(".dialog-close-button")) {
      dialog.close();
      return;
    }

    const inlineForm = dialogRoot.querySelector("form[data-inline-edit-form]:not([hidden])");
    if (inlineForm) {
      if (this.serializeForm(inlineForm) === inlineForm.dataset.inlineInitialState) {
        dialog.close();
        return;
      }
      this.openUnsavedChangesDialog({dialog, dialogRoot, trackedForm: inlineForm});
      return;
    }

    if (dialogRoot.dataset.dialogConfirmUnsavedChanges === "false" || dialogRoot.dataset.dialogSkipUnsavedConfirmation === "true" || dialogRoot.dataset.dialogHasTrackedForm !== "true" || trackedForm == null) {
      dialog.close();
      return;
    }

    const activeComposer = dialogRoot.querySelector("form.admin-comment-composer");
    if (activeComposer instanceof HTMLFormElement) trackedForm = activeComposer;

    const isDirty = this.isTrackedFormDirty(dialogRoot, trackedForm);
    const isValid = trackedForm.checkValidity();

    dialogRoot.dataset.dialogDirty = isDirty ? "true" : "false";
    dialogRoot.dataset.dialogValid = isValid ? "true" : "false";

    if (!isDirty) {
      dialog.close();
      return;
    }

    this.openUnsavedChangesDialog({
      dialog,
      dialogRoot,
      trackedForm,
      dialogSaveButton,
      initialFormState: dialogRoot.dataset.dialogInitialFormState || "",
      initialFileState: dialogRoot.dataset.dialogInitialFileState || "",
      submitButtons,
    });
  }

  isTrackedFormDirty(dialogRoot, trackedForm) {
    if (trackedForm.classList.contains("admin-comment-composer")) {
      const body = trackedForm.querySelector("textarea[name='admin_comment[body]']");
      const score = trackedForm.querySelector("input[name='admin_comment[score]']");
      return body?.value.trim().length > 0 || Number(score?.value || 0) > 0;
    }

    const initialFormState = dialogRoot.dataset.dialogInitialFormState || "";
    const initialFileState = dialogRoot.dataset.dialogInitialFileState || "";
    return this.serializeForm(trackedForm) !== initialFormState || this.serializeFileInputs(trackedForm) !== initialFileState;
  }

  resetCommentComposerState(dialogRoot, dialog) {
    const composer = dialogRoot.querySelector("form.admin-comment-composer");
    if (!(composer instanceof HTMLFormElement)) return null;

    dialogRoot.dataset.dialogInitialFormState = this.serializeForm(composer);
    dialogRoot.dataset.dialogInitialFileState = this.serializeFileInputs(composer);
    dialogRoot.dataset.dialogDirty = "false";
    dialogRoot.dataset.dialogValid = composer.checkValidity() ? "true" : "false";
    dialogRoot.classList.add("dialog-pristine");
    dialog.escapeKeyAction = "close";
    dialog.scrimClickAction = "close";
    return composer;
  }

  scrollToNewComment(dialogRoot) {
    const content = dialogRoot.querySelector(".comments-dialog__content")
      || dialogRoot.querySelector("[data-dialog-scroll-to-bottom='true']");
    const comment = dialogRoot.querySelector(".admin-comment--new");

    const scrollToBottom = (behavior) => {
      if (!(content instanceof HTMLElement)) return;
      content.scrollTo({top: content.scrollHeight, behavior});
    };
    const focusComposer = () => dialogRoot.querySelector(".admin-comment-composer textarea")?.focus({preventScroll: true});

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollToBottom("smooth"));
    });
    window.setTimeout(() => {
      scrollToBottom("auto");
      focusComposer();
    }, 350);

    if (comment instanceof HTMLElement) {
      window.setTimeout(() => comment.classList.remove("admin-comment--new"), 900);
    }
  }

  preventDialogClosing(dialog) {
    dialog.escapeKeyAction = "";
    dialog.scrimClickAction = "";
  }

  updateDialogSaveButtons(dialogRoot, trackedForm, submitButtons, initialFormState, initialFileState = "") {
    const isDirty = this.serializeForm(trackedForm) !== initialFormState || this.serializeFileInputs(trackedForm) !== initialFileState;
    const isValid = trackedForm.checkValidity();
    const isSubmitting = dialogRoot.dataset.dialogSubmitting === "true";
    const isEnabled = isDirty && isValid && !isSubmitting;

    submitButtons.forEach((button) => {
      button.disabled = !isEnabled;
      button.style.display = isDirty ? "" : "none";
    });

    trackedForm.querySelectorAll("[data-inline-edit-cancel]").forEach(button => {
      button.hidden = !isDirty;
      button.disabled = isSubmitting;
    });
    dialogRoot.dataset.dialogDirty = isDirty ? "true" : "false";
    dialogRoot.dataset.dialogValid = isValid ? "true" : "false";
  }

  openUnsavedChangesDialog({ dialog, dialogRoot, trackedForm, dialogSaveButton, initialFormState, initialFileState, submitButtons }) {
    if (dialogRoot.dataset.dialogConfirmationOpen === "true") {
      return;
    }

    const confirmationDialogTemplate = document.querySelector("#close-confirmation-dialog-template");
    if (confirmationDialogTemplate == null || trackedForm == null) {
      dialog.close();
      return;
    }

    const existingConfirmationDialog = document.querySelector("#close-confirmation-dialog");
    if (existingConfirmationDialog != null && existingConfirmationDialog.closest("#close-confirmation-dialog-template") == null) {
      return;
    }

    dialogRoot.dataset.dialogConfirmationOpen = "true";

    const div = document.createElement("div");
    div.innerHTML = confirmationDialogTemplate.innerHTML;
    const confirmationDialogEl = div.firstElementChild;
    if (confirmationDialogEl == null) return;

    if (dialogRoot.getAttribute("dir") == "rtl") {
      confirmationDialogEl.classList.add("rtl");
      confirmationDialogEl.setAttribute("dir", "rtl");
    }

    const titleEl = confirmationDialogEl.querySelector("#confirmation-dialog-title");
    const bodyEl = confirmationDialogEl.querySelector("#confirmation-dialog-body");
    const discardButton = confirmationDialogEl.querySelector(".confirmation-dialog__button--discard");
    const continueButton = confirmationDialogEl.querySelector(".confirmation-dialog__button--continue");

    if (titleEl != null) {
      titleEl.textContent = "Discard changes?";
    }

    if (bodyEl != null) {
      bodyEl.textContent = "You have unsaved changes.";
    }

    document.body.appendChild(confirmationDialogEl);
    const confirmationDialog = new MDCDialog(confirmationDialogEl);

    const closeConfirmation = () => {
      confirmationDialog.close();
      if (confirmationDialogEl.isConnected) {
        confirmationDialogEl.remove();
      }
      dialogRoot.dataset.dialogConfirmationOpen = "false";
    };

    discardButton?.addEventListener("click", () => {
      closeConfirmation();
      dialog.close();
    });

    continueButton?.addEventListener("click", () => {
      closeConfirmation();
    });

    confirmationDialog.listen("MDCDialog:closed", function () {
      if (confirmationDialogEl.isConnected) {
        confirmationDialogEl.remove();
      }
      dialogRoot.dataset.dialogConfirmationOpen = "false";
    });

    confirmationDialog.open();
    requestAnimationFrame(() => {
      continueButton?.focus();
    });
  }

  serializeForm(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  serializeFileInputs(form) {
    return Array.from(form.querySelectorAll("input[type='file']")).map((input) => {
      const files = Array.from(input.files || []);
      const fileState = files.map((file) => `${file.name}:${file.size}:${file.lastModified}`).join(",");
      return `${input.name}:${fileState}`;
    }).join("|");
  }

  getDialogSubmitButtons(dialogEl) {
    const buttons = Array.from(dialogEl.querySelectorAll("#dialog-submit-button, [data-mdc-dialog-save-button='true'], .dialog-submit-button, .dialog-submit-form button[type='submit'], .dialog-submit-form input[type='submit']"));

    dialogEl.querySelectorAll("form.dialog-submit-form[id]").forEach(function (form) {
      buttons.push(...Array.from(dialogEl.querySelectorAll(`button[type='submit'][form='${form.id}'], input[type='submit'][form='${form.id}']`)));
    });

    return buttons.filter(function (button, index, array) {
      return array.indexOf(button) === index;
    });
  }

}
